import {
  Flag,
  FLAG_SEVERITY,
  FlagKind,
  PATH_SEPARATOR,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  SpineLevel,
  effectiveAttrs,
  isPlaceholderValue,
  levelsForScope,
} from "../domain/studioTypes";
import "./vendor/cdt-resolve.bundle.js";
import { GRADIENT_PIN_THRESHOLD, PLACEHOLDER_VALUE, type ResolverPin } from "./cdtResolve";
import { baseSkuId, toResolverInputs, type ResolverInputs } from "./toResolverInputs";
import { compactRowsOf } from "../domain/shelfRankSpace";
import { nodeValueOrder } from "../domain/nodeValueOrder";
import { explainOrderViolation, explanationBy } from "../domain/orderExplainer";
import { verticalFragments } from "./blockContiguity";
import { columnValueBreaks } from "./columnContiguity";
import { slacksOf } from "../domain/bayShare";
import { planBayWidthOf } from "@CommonUtils/cdtStudio/domain/aestheticsScore";
import { sizeFamilyOf } from "@CommonUtils/cdtStudio/domain/sizeFamily";
import { dropExplainedStrays, splitOffBrands } from "./brandSplitOff";
import { rowLineFlags } from "../domain/rowReplication";
import { avoidableWithinFamilies, sizeFamilyKeyOf, sizePairComparable } from "./sizeOrdering";
import {
  detailText,
  englishFlagTemplate,
  evidenceText,
  phrase,
  phraseText,
  type FlagEvidenceWords,
  type FlagPhrase,
  type FlagWords,
} from "../domain/flagWords";
import { boardIndexAt, rowWindowsOf } from "@CommonUtils/cdtStudio/domain/wallRows";

const RESOLVER_SIZE_ATTR = "__sizeLabel";

interface PlanItem {
  id: string;
  run: number;
  shelf: number;
  x: number;
  w: number;
  hf: number;
  path: readonly (string | null)[];
}
interface PlanModel {
  spine: readonly string[];
  /** The plan's own bay, raw units. */
  bayW?: number;
  all: () => PlanItem[];
  lane: (run: number, shelf: number) => PlanItem[];
  get: (id: string) => PlanItem | undefined;
  cdtCompare: (a: PlanItem, b: PlanItem) => number;
  nodeKey: (item: PlanItem, depth: number) => string;
}
interface CdtResolveInternals {
  loadPlan: (canvas: unknown, itemsDoc: unknown, opts?: unknown) => PlanModel;
  Plan: { right: (item: PlanItem) => number; width: (item: PlanItem) => number };
  audit: {
    splitNodes: (
      plan: PlanModel,
      depth: number,
    ) => readonly { depth: number; node: string; blocks: number }[];
    /** Per node, the run-wide x-stretches `splitNodes` counts. */
    stretches: (
      plan: PlanModel,
      depth: number,
    ) => ReadonlyMap<string, readonly { run: number; x0: number; x1: number }[]>;
    audit: (plan: PlanModel) => {
      splits: readonly { depth: number; node: string; blocks: number }[];
      laneBreaks: readonly {
        depth: number;
        node: string;
        run: number;
        shelf: number;
        foreign: number;
      }[];
    };
    sequenceBreaks: (
      plan: PlanModel,
      ids: readonly string[],
      orientation: string,
    ) => { inversions: number; order: readonly string[] };
  };
  grade: {
    blocksAt: (plan: PlanModel) => Map<string, string[]>;
    inversions: (
      plan: PlanModel,
      ids: readonly string[],
    ) => { count: number; forced: number; avoidable: number };
  };
}

export interface DetectedExceptions {
  flags: readonly Flag[];
  pinByFlagId: Readonly<Record<string, ResolverPin>>;
  inputs: ResolverInputs;
}

export interface DetectCoreOptions {
  runW?: number;
  bayW?: number;
  /** The wall's boards. Absent, every row is a board the run's whole width. */
  runs?: readonly StudioRunGeometry[];
  labelAt?: (depth: number) => string | undefined;
  /** On for as-built; off after regenerate — residual gradient has no offered fix. */
  gradientPins?: boolean;
  sizeRule?: "listed";
  orphanDepths?: readonly number[];
  /** Repeat across two identity levels is a mis-tag; sharing an ornament is noise. */
  crossLevelIdentityDepths?: readonly number[];
  /** Off in the studio: "X used at two levels (Form, Pack type)" fires on every pack word the
   *  sheet repeats across two columns and is review noise there; kept for callers that want it. */
  crossLevelNameFlags?: boolean;
  terminalVariantDepth?: number | null;
  /** The canvas shelf -> the row rank a merchant reads, when the canvas skips empty hang lines. */
  rankOfShelf?: (shelf: number) => number;
}

interface ScatterSplit {
  node: string;
  depth: number;
  blocks: number;
  ids: string[];
}

const scatterSplits = (
  cdtResolve: CdtResolveInternals,
  plan: PlanModel,
  depths: readonly number[],
  skip: (node: string) => boolean = () => false,
): ScatterSplit[] => {
  const { Plan } = cdtResolve;
  const idsUnderNode = (node: string, depth: number): string[] =>
    plan
      .all()
      .filter((item) => plan.nodeKey(item, depth) === node)
      .map((item) => item.id);
  const out: ScatterSplit[] = [];
  for (const depth of depths) {
    for (const split of cdtResolve.audit.splitNodes(plan, depth)) {
      if (skip(split.node)) continue;
      // Shelf wrap is x-split but fine; scatter is members non-consecutive in reading order.
      const parentNode = split.node.split(PATH_SEPARATOR).slice(0, -1).join(PATH_SEPARATOR);
      const parentIds = idsUnderNode(parentNode, depth - 1);
      const reading = cdtResolve.audit.sequenceBreaks(plan, parentIds, "horizontal").order;
      const positions = reading
        .map((id, index) => ({ id, index }))
        .filter((entry) => {
          const item = plan.get(entry.id);
          return item && plan.nodeKey(item, depth) === split.node;
        })
        .map((entry) => entry.index);
      const consecutive =
        positions.length < 2 ||
        Math.max(...positions) - Math.min(...positions) === positions.length - 1;
      // Shared x-window = a vertical box, not scatter.
      const byShelf = new Map<string, { min: number; max: number }>();
      for (const item of plan.all()) {
        if (plan.nodeKey(item, depth) !== split.node) continue;
        const key = `${item.run}|${item.shelf}`;
        const entry = byShelf.get(key) ?? { min: Infinity, max: -Infinity };
        entry.min = Math.min(entry.min, item.x);
        entry.max = Math.max(entry.max, Plan.right(item));
        byShelf.set(key, entry);
      }
      const intervals = [...byShelf.values()];
      const common = {
        min: Math.max(...intervals.map((interval) => interval.min)),
        max: Math.min(...intervals.map((interval) => interval.max)),
      };
      const widths = intervals.map((interval) => interval.max - interval.min).sort((a, b) => a - b);
      const median = widths[Math.floor(widths.length / 2)] || slacksOf(plan.bayW).edge;
      const boxed = intervals.length >= 2 && common.max - common.min >= 0.4 * median;
      if (consecutive || boxed) continue;
      out.push({
        node: split.node,
        depth,
        blocks: split.blocks,
        ids: idsUnderNode(split.node, depth),
      });
    }
  }
  return out;
};

/** A flag's words, and the English `title`, `detail` and `fixLabel` made from them. */
const worded = (words: FlagWords): Pick<Flag, "title" | "detail" | "fixLabel" | "words"> => ({
  title: phraseText(words.title, englishFlagTemplate),
  detail: detailText(words, englishFlagTemplate),
  fixLabel: words.fixLabel ? phraseText(words.fixLabel, englishFlagTemplate) : null,
  words,
});

/** `flag` under a new title, its detail kept. */
const retitled = (flag: Flag, title: FlagPhrase): Pick<Flag, "title" | "words"> => ({
  title: phraseText(title, englishFlagTemplate),
  ...(flag.words ? { words: { ...flag.words, title } } : {}),
});

const shelvesPhrase = (shelves: readonly number[], joiner = ", "): FlagPhrase =>
  phrase("shelves", { shelves: shelves.join(joiner) }, shelves.length);

/** "shelf 6", "shelves 1–3" for a run of shelves, "shelves 2, 5" when they are not adjacent. */
const shelfSpan = (shelves: readonly number[]): FlagPhrase => {
  const sorted = [...shelves].sort((a, b) => a - b);
  const adjacent = sorted.every((shelf, index) => index === 0 || shelf === sorted[index - 1] + 1);
  return sorted.length > 1 && adjacent
    ? phrase("shelfRange", { from: sorted[0], to: sorted[sorted.length - 1] })
    : shelvesPhrase(sorted);
};

const pairsAgainstOrder = (n: number): FlagPhrase => phrase("pairsOutOfFlow", undefined, n);

const outOfPlace = (values: readonly string[]): FlagPhrase =>
  phrase("outOfPlace", { values: values.join(", ") });

/** "Size" and "Quantity" read as "sizes"; any other quantity label is used as it is. */
const sizeOrderTitle = (block: string, label: string): FlagPhrase =>
  /^(size|quantity)$/i.test(label.trim())
    ? phrase("sizeOrderTitle", { block })
    : phrase("sizeOrderLabelTitle", { block, label: label.toLowerCase() });

const scatterWords = (scope: readonly string[], blocks: number): FlagWords => ({
  title: phrase("scatterTitle", {
    block: collapsePathRepeats([...scope]).join(PATH_SEPARATOR),
    count: blocks,
  }),
  detail: [phrase("scatterDetail")],
});

const bandHoleWords = (
  block: string,
  occupied: readonly number[],
  holes: readonly number[],
): FlagWords => {
  const missing = shelvesPhrase([...new Set(holes)].sort((a, b) => a - b));
  return {
    title: phrase("bandHoleTitle", { block, holes: missing }),
    detail: [phrase("bandHoleDetail", { occupied: shelvesPhrase(occupied), holes: missing })],
  };
};

export const collapsePathRepeats = (segments: readonly string[]): string[] => {
  const out: string[] = [];
  for (const segment of segments) {
    const previous = out[out.length - 1];
    if (previous && (previous === segment || previous.endsWith(` ${segment}`))) continue;
    out.push(segment);
  }
  return out;
};

export const detectCore = (
  canvas: ResolverInputs["canvas"],
  itemsDoc: ResolverInputs["itemsDoc"],
  options: DetectCoreOptions = {},
): {
  flags: Flag[];
  pinByFlagId: Record<string, ResolverPin>;
  /** Three scans share `split`; rekey/rescope must only see their own claim. */
  bandHoleFlagIds: ReadonlySet<string>;
  scatterFlagIds: ReadonlySet<string>;
  /** "Sits in N separate stretches" flags: the plan ids standing in each stretch. */
  splitStretches: Readonly<Record<string, readonly (readonly string[])[]>>;
} => {
  const bandHoleFlagIds = new Set<string>();
  const scatterFlagIds = new Set<string>();
  const rankOf = options.rankOfShelf ?? ((shelf: number) => shelf);
  const rowsOf = (shelves: readonly number[]) => shelves.map(rankOf);
  const splitStretches: Record<string, string[][]> = {};
  const none = { flags: [], pinByFlagId: {}, bandHoleFlagIds, scatterFlagIds, splitStretches };
  const cdtResolve = (globalThis as { CDTResolve?: CdtResolveInternals }).CDTResolve;
  if (!cdtResolve) return none;

  let plan: PlanModel;
  try {
    const opts = {
      ...(options.runW ? { runW: options.runW } : {}),
      ...(options.sizeRule ? { sizeRule: options.sizeRule } : {}),
    };
    plan = cdtResolve.loadPlan(canvas, itemsDoc, Object.keys(opts).length ? opts : undefined);
  } catch {
    return none;
  }
  const { Plan } = cdtResolve;
  const labelAt = options.labelAt ?? ((depth: number) => itemsDoc.LABELS?.[plan.spine[depth]]);

  const flags: Flag[] = [];
  const pinByFlagId: Record<string, ResolverPin> = {};
  // The plan's own bay, as the canvas states it; a plan with no bays reads `planBayWidthOf`'s
  // stand-in.
  const bayW = options.bayW || canvas.bayW || planBayWidthOf([]);
  const slack = slacksOf(bayW);
  /** A product as a category manager reads it: by its name, by its code only without one. */
  const productNames = new Map(
    canvas.runs.flatMap((run) => run.items.map((item) => [item.id, item.desc?.trim()] as const)),
  );
  const productNameOf = (id: string) => productNames.get(id) || baseSkuId(id);

  // Display only: ghost slots stay on pin/id so they still match vendored `nodeKey`.
  const studioScopePath = (scope: string): string[] =>
    scope
      .split(PATH_SEPARATOR)
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0);

  const shown = (scope: string): string =>
    collapsePathRepeats(studioScopePath(scope)).join(PATH_SEPARATOR);

  const pushedIds = new Set<string>();
  const shownIds = new Set<string>();
  const push = (
    kind: FlagKind,
    scope: string,
    ids: readonly string[],
    words: FlagWords,
    withSeqOrder: boolean,
    extras?: {
      pinKind?: string;
      evidence?: Flag["evidence"];
      anchorId?: string;
      fragment?: boolean;
      levelLabel?: string;
      severity?: Flag["severity"];
      describeOnly?: boolean;
    },
  ) => {
    const id = `${kind}:${scope}`;
    if (pushedIds.has(id)) return;
    pushedIds.add(id);
    // Compacted path can collide across depths; raw `id` keeps every slot.
    const shownId = `${kind}:${studioScopePath(scope).join(PATH_SEPARATOR)}`;
    if (shownIds.has(shownId)) return;
    shownIds.add(shownId);
    const skuIds = [...new Set(ids.map(baseSkuId))];
    flags.push({
      id,
      kind,
      ...(extras?.levelLabel ? { levelLabel: extras.levelLabel } : {}),
      severity: extras?.severity ?? FLAG_SEVERITY[kind],
      scopePath: studioScopePath(scope),
      ...worded(words),
      skuIds,
      anchorSkuId: extras?.anchorId ? baseSkuId(extras.anchorId) : (skuIds[0] ?? ""),
      evidence: extras?.evidence ?? {},
      fix: null,
    });
    if (extras?.describeOnly) return;
    pinByFlagId[id] = {
      kind: extras?.pinKind ?? kind,
      scope,
      node: scope,
      ids,
      ...(withSeqOrder && ids.length > 1 ? { seqOrder: ids } : {}),
      ...(extras?.fragment ? { fragment: true } : {}),
    };
  };

  const idsUnderNode = (node: string, depth: number): string[] =>
    plan
      .all()
      .filter((item) => plan.nodeKey(item, depth) === node)
      .map((item) => item.id);

  const runById = new Map((options.runs ?? []).map((run) => [run.run, run]));
  /** Whether a row has a board anywhere under [left, right) of a run. */
  const hasBoardUnder = (run: number, rank: number, left: number, right: number): boolean => {
    const geometry = runById.get(run);
    if (!geometry) return true;
    return rowWindowsOf(geometry).some(
      (window) => window.rank === rank && window.startCm < right && window.endCm > left,
    );
  };

  const RAGGED_EDGE_FRAC = 0.15;
  const blockShapeAt = (node: string, depth: number): "split" | "ragged" | "clean" => {
    const members = plan.all().filter((item) => plan.nodeKey(item, depth) === node);
    if (members.length < 2) return "clean";
    const byRun = new Map<number, Map<number, PlanItem[]>>();
    for (const item of members) {
      const shelves = byRun.get(item.run) ?? new Map<number, PlanItem[]>();
      shelves.set(item.shelf, [...(shelves.get(item.shelf) ?? []), item]);
      byRun.set(item.run, shelves);
    }
    let edgeSpread = 0;
    let widest = 0;
    for (const [run, shelves] of byRun) {
      const ranks = [...shelves.keys()].sort((a, b) => a - b);
      const placed = [...shelves.values()].flat();
      const left = Math.min(...placed.map((item) => item.x));
      const right = Math.max(...placed.map((item) => Plan.right(item)));
      // A row with no board under the block is the wall's gap, not the block's.
      for (let rank = ranks[0]; rank <= ranks[ranks.length - 1]; rank += 1) {
        if (!shelves.has(rank) && hasBoardUnder(run, rank, left, right)) return "split";
      }
      const lefts: number[] = [];
      const rights: number[] = [];
      for (const rank of ranks) {
        const own = new Set((shelves.get(rank) ?? []).map((item) => item.id));
        const lane = plan.lane(run, rank);
        const first = lane.findIndex((item) => own.has(item.id));
        let last = -1;
        lane.forEach((item, index) => {
          if (own.has(item.id)) last = index;
        });
        if (first < 0) continue;
        for (let index = first; index <= last; index += 1) {
          if (!own.has(lane[index].id)) return "split";
        }
        // Facings either side of a bay with no board on this row are two pieces.
        const boards = new Set(
          lane.slice(first, last + 1).map((item) => boardIndexAt(runById.get(run), rank, item.x)),
        );
        if (boards.size > 1) return "split";
        lefts.push(lane[first].x);
        rights.push(Plan.right(lane[last]));
        widest = Math.max(widest, Plan.right(lane[last]) - lane[first].x);
      }
      if (lefts.length > 1) {
        edgeSpread = Math.max(
          edgeSpread,
          Math.max(...lefts) - Math.min(...lefts),
          Math.max(...rights) - Math.min(...rights),
        );
      }
    }
    return edgeSpread > Math.max(widest * RAGGED_EDGE_FRAC, bayW * RAGGED_EDGE_FRAC)
      ? "ragged"
      : "clean";
  };

  const audit = cdtResolve.audit.audit(plan);
  const splitNodes = new Set<string>();
  const stretchesAt = new Map<number, ReturnType<CdtResolveInternals["audit"]["stretches"]>>();
  const stretchIdsOf = (node: string, depth: number): string[][] => {
    const byNode = stretchesAt.get(depth) ?? cdtResolve.audit.stretches(plan, depth);
    stretchesAt.set(depth, byNode);
    const segments = byNode.get(node) ?? [];
    const ids: string[][] = segments.map(() => []);
    for (const item of plan.all()) {
      if (plan.nodeKey(item, depth) !== node) continue;
      const at = segments.findIndex(
        (segment) =>
          segment.run === item.run &&
          item.x >= segment.x0 - slack.fill &&
          item.x <= segment.x1 + slack.fill,
      );
      if (at >= 0) ids[at].push(item.id);
    }
    return ids;
  };
  for (const split of audit.splits) {
    const shape = blockShapeAt(split.node, split.depth);
    if (shape === "ragged") {
      // Untidy is not an exception.
      continue;
    }
    splitNodes.add(split.node);
    push(
      "split",
      split.node,
      idsUnderNode(split.node, split.depth),
      {
        title: phrase("splitTitle", { block: shown(split.node), count: split.blocks }),
        detail: [phrase("splitDetail")],
      },
      false,
    );
    splitStretches[`split:${split.node}`] = stretchIdsOf(split.node, split.depth);
  }

  // Band holes: x-interval census cannot see a missing shelf inside the band.
  for (const depth of options.orphanDepths ?? [1, 2]) {
    const nodes = new Set<string>();
    for (const item of plan.all()) nodes.add(plan.nodeKey(item, depth));
    for (const node of nodes) {
      if (!node || splitNodes.has(node)) continue;
      const leaf = node.split(PATH_SEPARATOR).pop() ?? "";
      if (!leaf || PLACEHOLDER_VALUE.test(leaf)) continue;
      const members = plan.all().filter((item) => plan.nodeKey(item, depth) === node);
      if (members.length < 2) continue;
      const byRun = new Map<number, PlanItem[]>();
      for (const item of members) byRun.set(item.run, [...(byRun.get(item.run) ?? []), item]);
      const holes: number[] = [];
      for (const [run, placed] of byRun) {
        const shelves = new Set(placed.map((item) => item.shelf));
        const ranks = [...shelves].sort((a, b) => a - b);
        const left = Math.min(...placed.map((item) => item.x));
        const right = Math.max(...placed.map((item) => Plan.right(item)));
        for (let rank = ranks[0]; rank <= ranks[ranks.length - 1]; rank += 1) {
          if (!shelves.has(rank) && hasBoardUnder(run, rank, left, right)) holes.push(rank);
        }
      }
      if (holes.length === 0) continue;
      splitNodes.add(node);
      bandHoleFlagIds.add(`split:${node}`);
      const occupied = [...new Set(members.map((item) => item.shelf))].sort((a, b) => a - b);
      push(
        "split",
        node,
        members.map((item) => item.id),
        bandHoleWords(shown(node), rowsOf(occupied), rowsOf(holes)),
        false,
      );
    }
  }

  for (const scatter of scatterSplits(
    cdtResolve,
    plan,
    (options.orphanDepths ?? [1, 2, 3, 4]).filter((value) => value > 2),
    (node) => splitNodes.has(node),
  )) {
    splitNodes.add(scatter.node);
    scatterFlagIds.add(`split:${scatter.node}`);
    push(
      "split",
      scatter.node,
      scatter.ids,
      scatterWords(studioScopePath(scatter.node), scatter.blocks),
      false,
      { pinKind: "orphan", fragment: true },
    );
  }

  const breaksByNode = new Map<string, { depth: number; run: number; shelf: number }[]>();
  for (const breakEntry of audit.laneBreaks) {
    if (splitNodes.has(breakEntry.node)) continue;
    const list = breaksByNode.get(breakEntry.node) ?? [];
    list.push(breakEntry);
    breaksByNode.set(breakEntry.node, list);
  }
  breaksByNode.forEach((lanes, node) => {
    const depth = Math.min(...lanes.map((lane) => lane.depth));
    const intruders: PlanItem[] = [];
    for (const laneEntry of lanes) {
      const arr = plan.lane(laneEntry.run, laneEntry.shelf);
      let first = -1;
      let last = -1;
      arr.forEach((item, index) => {
        if (plan.nodeKey(item, laneEntry.depth) === node) {
          if (first < 0) first = index;
          last = index;
        }
      });
      for (let index = first; index >= 0 && index <= last; index++) {
        if (plan.nodeKey(arr[index], laneEntry.depth) !== node) intruders.push(arr[index]);
      }
    }
    if (!intruders.length) return;
    const intruderNodes = [...new Set(intruders.map((item) => plan.nodeKey(item, depth)))];
    const shelves = [...new Set(lanes.map((lane) => lane.shelf))].sort((a, b) => a - b);
    push(
      "contiguity",
      node,
      intruders.map((item) => item.id),
      {
        title: phrase(
          "contiguityTitle",
          { blocks: { list: intruderNodes.map(shown) }, block: shown(node) },
          intruderNodes.length,
        ),
        detail: [
          phrase("contiguityDetail", { shelves: shelvesPhrase(rowsOf(shelves)) }, intruders.length),
        ],
      },
      false,
      {
        // Victim split pin would no-op; relocate the intruders.
        pinKind: "orphan",
        anchorId: intruders[0].id,
        evidence: { notes: intruderNodes.map((name) => `intruder: ${shown(name)}`) },
      },
    );
  });

  const gradeDepth = Math.max(1, plan.spine.indexOf(RESOLVER_SIZE_ATTR));
  for (const fragment of verticalFragments(plan, gradeDepth, Plan)) {
    const shelfLabel = rowsOf(fragment.shelves).join("–");
    const scope = `${fragment.node}${PATH_SEPARATOR}shelf ${shelfLabel}`;
    const where =
      fragment.run === fragment.mainRun
        ? `shelf ${shelfLabel}`
        : `another aisle (run ${fragment.run + 1}, shelf ${shelfLabel})`;
    const whereShort = phrase(fragment.run === fragment.mainRun ? "whereShelf" : "whereAisle", {
      shelf: shelfLabel,
    });
    const facings = fragment.ids.length;
    push(
      "split",
      scope,
      fragment.ids,
      {
        title: phrase("fragmentTitle", { block: shown(fragment.node), where: whereShort }, facings),
        detail: [
          phrase(
            "fragmentDetail",
            { span: shelfSpan(rowsOf(fragment.mainShelves)), where: whereShort },
            facings,
          ),
        ],
      },
      false,
      {
        pinKind: "orphan",
        anchorId: fragment.ids[0],
        fragment: true,
        evidence: {
          notes: [
            `block band: shelves ${rowsOf(fragment.mainShelves).join("–")}`,
            `fragment: ${where}`,
          ],
        },
      },
    );
  }

  const columnBreaks = Array.from({ length: Math.max(0, gradeDepth) }, (_, depth) => depth)
    .filter((depth) => depth >= 1)
    .flatMap((depth) => columnValueBreaks(plan, depth, Plan));
  for (const columnBreak of columnBreaks) {
    const upperFirst =
      columnBreak.homeShelves[0] < columnBreak.strayShelves[0]
        ? [columnBreak.homeShelves, columnBreak.strayShelves]
        : [columnBreak.strayShelves, columnBreak.homeShelves];
    const strayLabel = rowsOf(columnBreak.strayShelves).join("–");
    const scope = `${columnBreak.strayNode}${PATH_SEPARATOR}shelf ${strayLabel}`;
    const between = columnBreak.between.join(", ");
    // Compact before the positional head/tail trim — a ghost slot would misalign it.
    const homeParts = collapsePathRepeats(studioScopePath(columnBreak.homeNode));
    const strayParts = collapsePathRepeats(studioScopePath(columnBreak.strayNode));
    let head = 0;
    while (
      head < homeParts.length - 1 &&
      head < strayParts.length - 1 &&
      homeParts[head] === strayParts[head]
    )
      head++;
    let tail = 0;
    while (
      tail < homeParts.length - head - 1 &&
      tail < strayParts.length - head - 1 &&
      homeParts[homeParts.length - 1 - tail] === strayParts[strayParts.length - 1 - tail]
    )
      tail++;
    const shortName = (parts: string[]) =>
      parts.slice(head, parts.length - tail).join(PATH_SEPARATOR) || parts.join(PATH_SEPARATOR);
    const blockName = (parts: string[]) => {
      const distinct = parts.slice(head, parts.length - tail);
      const kept = distinct.length ? distinct : parts;
      return kept[kept.length - 1] ?? "";
    };
    const upperName =
      columnBreak.homeShelves[0] < columnBreak.strayShelves[0] ? homeParts : strayParts;
    const lowerName = upperName === homeParts ? strayParts : homeParts;
    const wall = `${shortName(upperName)} over ${shortName(lowerName)}`;
    const upperBlock = blockName(upperName);
    const lowerBlock = blockName(lowerName);
    const upperNodeKey =
      columnBreak.homeShelves[0] < columnBreak.strayShelves[0]
        ? columnBreak.homeNode
        : columnBreak.strayNode;
    const lowerNodeKey =
      upperNodeKey === columnBreak.homeNode ? columnBreak.strayNode : columnBreak.homeNode;
    const upperShelves = new Set(upperFirst[0]);
    // Wrap only if the lower block's own facings sit beside the upper on those shelves.
    const bandItems = [...columnBreak.homeIds, ...columnBreak.strayIds]
      .map((id) => plan.get(id))
      .filter(Boolean) as PlanItem[];
    const windowX0 = Math.min(...bandItems.map((item) => item.x));
    const windowX1 = Math.max(...bandItems.map((item) => Plan.right(item)));
    const lowerBesideUpper =
      bandItems.length > 0 &&
      plan
        .all()
        .some(
          (item) =>
            item.run === columnBreak.run &&
            upperShelves.has(item.shelf) &&
            plan.nodeKey(item, columnBreak.depth) === lowerNodeKey &&
            item.x <= windowX1 + Plan.width(item) &&
            Plan.right(item) >= windowX0 - Plan.width(item),
        );
    const squash = (text: string) => text.replace(/\s+/g, " ");
    const headline = {
      lower: squash(lowerBlock),
      upper: squash(upperBlock),
      value: squash(columnBreak.value),
    };
    const evidence: FlagEvidenceWords = {
      built: [
        phrase("blockOnShelves", {
          block: shown(columnBreak.homeNode),
          shelves: shelvesPhrase(rowsOf(columnBreak.homeShelves), "–"),
        }),
        phrase("blockOnShelves", {
          block: shown(columnBreak.strayNode),
          shelves: shelvesPhrase(rowsOf(columnBreak.strayShelves), "–"),
        }),
      ],
      want: [phrase(lowerBesideUpper ? "wrapWant" : "columnWant", { value: columnBreak.value })],
    };
    push(
      "column",
      scope,
      columnBreak.strayIds,
      lowerBesideUpper
        ? {
            title: phrase("wrapTitle", headline),
            detail: [phrase("wrapDetail", { lower: lowerBlock, upper: upperBlock })],
            evidence,
          }
        : {
            title: phrase("columnTitle", headline),
            detail: [
              phrase("columnDetail", {
                value: columnBreak.value,
                upperShelves: shelvesPhrase(rowsOf(upperFirst[0]), "–"),
                lowerShelves: shelvesPhrase(rowsOf(upperFirst[1]), "–"),
                between,
              }),
            ],
            evidence,
          },
      false,
      {
        // Wrap is described, not dispatched — no repair.
        ...(lowerBesideUpper ? { severity: "toReview" as const, describeOnly: true } : {}),
        anchorId: columnBreak.strayIds[0],
        evidence: {
          ...evidenceText(evidence, englishFlagTemplate),
          notes: [`column: ${wall}`, `between the bands: ${between}`],
        },
      },
    );
  }

  // Edge SKUs sit a bay from kin — do not flag them.
  const ORPHAN_MIN_BAYS = 1;
  const all = plan.all();
  const orphanFlagged = new Set<string>();
  const orphanDepths = options.orphanDepths?.length ? options.orphanDepths : [1, 2];
  for (const depth of orphanDepths) {
    for (const item of all) {
      if (orphanFlagged.has(item.id)) continue;
      const key = item.path.slice(0, depth).join(PATH_SEPARATOR);
      const family = all.filter(
        (other) => other.id !== item.id && other.path.slice(0, depth).join(PATH_SEPARATOR) === key,
      );
      if (!family.length) continue;
      const familyIds = new Set(family.map((other) => other.id));
      const lane = plan.lane(item.run, item.shelf);
      const index = lane.findIndex((other) => other.id === item.id);
      const sides = [lane[index - 1], lane[index + 1]].filter(Boolean) as PlanItem[];
      if (sides.some((side) => familyIds.has(side.id))) continue;
      const sameRun = family.filter((other) => other.run === item.run);
      const bays = sameRun.length
        ? Math.min(
            ...sameRun.map((other) =>
              Math.max(item.x - Plan.right(other), other.x - Plan.right(item), 0),
            ),
          ) / bayW
        : null;
      if (bays != null && bays < ORPHAN_MIN_BAYS) continue;
      orphanFlagged.add(item.id);
      const ownScope = item.path
        .slice(0, Math.min(Math.max(...orphanDepths), plan.spine.length))
        .map((value) => value ?? "")
        .join(PATH_SEPARATOR);
      push(
        "orphan",
        ownScope,
        [item.id],
        {
          title:
            bays == null
              ? phrase("orphanAisleTitle", { product: productNameOf(item.id), block: shown(key) })
              : phrase("orphanBaysTitle", {
                  product: productNameOf(item.id),
                  bays: bays.toFixed(1),
                  block: shown(key),
                }),
          detail: [phrase("orphanDetail")],
        },
        false,
      );
    }
  }

  const sizeIndex = plan.spine.indexOf(RESOLVER_SIZE_ATTR);
  const terminalVariantDepth =
    options.terminalVariantDepth === undefined
      ? plan.spine.length - 1
      : options.terminalVariantDepth;

  const laneEvidence = (
    arr: readonly PlanItem[],
    depth: number,
  ): { built: string[]; want: string[]; offenders: string[] } => {
    const built: string[] = [];
    const representative = new Map<string, PlanItem>();
    for (const item of arr) {
      const value = String(item.path[depth] ?? "");
      if (built[built.length - 1] !== value) built.push(value);
      if (!representative.has(value)) representative.set(value, item);
    }
    const firstSeen = [...new Set(built)];
    const want = [...firstSeen].sort((a, b) =>
      plan.cdtCompare(representative.get(a)!, representative.get(b)!),
    );
    const returned = new Set<string>();
    const seen = new Set<string>();
    for (const value of built) {
      if (seen.has(value)) returned.add(value);
      seen.add(value);
    }
    const offenders = [
      ...new Set([...firstSeen.filter((value, index) => value !== want[index]), ...returned]),
    ];
    return { built, want, offenders };
  };

  const groupEvidence = (
    items: readonly PlanItem[],
    depth: number,
  ): { built: string[]; want: string[]; offenders: string[] } => {
    const groups = new Map<string, { sumX: number; width: number; rep: PlanItem }>();
    for (const item of items) {
      const value = String(item.path[depth] ?? "");
      const width = item.w * item.hf;
      const entry = groups.get(value);
      if (entry) {
        entry.sumX += (item.x + width / 2) * width;
        entry.width += width;
      } else {
        groups.set(value, { sumX: (item.x + width / 2) * width, width, rep: item });
      }
    }
    const built = [...groups.entries()]
      .sort((a, b) => a[1].sumX / a[1].width - b[1].sumX / b[1].width)
      .map(([value]) => value);
    const want = [...built].sort((a, b) => plan.cdtCompare(groups.get(a)!.rep, groups.get(b)!.rep));
    const offenders = built.filter((value, index) => value !== want[index]);
    return { built, want, offenders };
  };

  /** Inside a quantity band the next key orders; do not demand numeric size. */
  const bandParentMemo = new Map<string, boolean>();
  const underQuantityBand = (node: string): boolean => {
    const segments = node.split(PATH_SEPARATOR);
    for (let depth = 1; depth <= segments.length; depth += 1) {
      const parentKey = segments.slice(0, depth - 1).join(PATH_SEPARATOR);
      const memoKey = `${depth}|${parentKey}`;
      let banded = bandParentMemo.get(memoKey);
      if (banded === undefined) {
        const byValue = new Map<string, number[]>();
        for (const item of plan.all()) {
          if (item.path.slice(0, depth - 1).join(PATH_SEPARATOR) !== parentKey) continue;
          const value = String(item.path[depth - 1] ?? "");
          if (!value || PLACEHOLDER_VALUE.test(value)) continue;
          const parsed = sizeFamilyOf(String(item.path[sizeIndex] ?? ""));
          if (!(parsed.magnitude > 0)) continue;
          byValue.set(value, [...(byValue.get(value) ?? []), parsed.magnitude]);
        }
        const groups = [...byValue.values()].filter((magnitudes) => magnitudes.length >= 2);
        if (groups.length < 2) {
          banded = false;
        } else {
          const quantile = (sorted: number[], q: number) =>
            sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];
          const spans = groups
            .map((magnitudes) => {
              const sorted = [...magnitudes].sort((a, b) => a - b);
              return { lo: quantile(sorted, 0.1), hi: quantile(sorted, 0.9) };
            })
            .sort((a, b) => a.lo - b.lo);
          banded = spans.every((span, index) => index === 0 || spans[index - 1].hi < span.lo);
        }
        bandParentMemo.set(memoKey, banded);
      }
      if (banded) return true;
    }
    return false;
  };

  cdtResolve.grade.blocksAt(plan).forEach((ids, node) => {
    if (ids.length < 2) return;
    if (underQuantityBand(node)) return;
    // Within unit families — mixed counts/oz/fl-oz is not a ladder.
    const avoidable = avoidableWithinFamilies(plan, ids, sizeIndex, (model, familyIds) =>
      cdtResolve.grade.inversions(model as unknown as PlanModel, familyIds),
    );
    let along = 0;
    const lanes = new Map<string, PlanItem[]>();
    for (const id of ids) {
      const it = plan.get(id);
      if (!it) continue;
      const key = `${it.run}|${it.shelf}`;
      const laneItems = lanes.get(key) ?? [];
      laneItems.push(it);
      lanes.set(key, laneItems);
    }
    let worstLane: PlanItem[] | null = null;
    let worstBad = 0;
    lanes.forEach((arr) => {
      arr.sort((a, b) => a.x - b.x);
      let laneBad = 0;
      for (let i = 1; i < arr.length; i++) {
        const a = arr[i - 1];
        const b = arr[i];
        if (String(a.path[sizeIndex]) === String(b.path[sizeIndex])) continue;
        // Unknown size is never a violation.
        if (!sizePairComparable(a.path[sizeIndex], b.path[sizeIndex])) continue;
        if (plan.cdtCompare(a, b) > 0) laneBad++;
      }
      along += laneBad;
      if (laneBad > worstBad) {
        worstBad = laneBad;
        worstLane = arr;
      }
    });
    // Small vertical residue is reported, not flagged.
    if (along === 0 && (options.gradientPins === false || avoidable < GRADIENT_PIN_THRESHOLD))
      return;
    const allBlockItems = ids.map((id) => plan.get(id)).filter(Boolean) as PlanItem[];
    const levelIsMeasured = allBlockItems.some(
      (item) => sizeFamilyKeyOf(item.path[sizeIndex]) !== "",
    );
    // Names-only levels order by the stored list, not size.
    if (!levelIsMeasured) return;
    const graded = (item: PlanItem) =>
      !levelIsMeasured || sizeFamilyKeyOf(item.path[sizeIndex]) !== "";
    const laneEv =
      worstLane && worstBad > 0
        ? laneEvidence((worstLane as PlanItem[]).filter(graded), sizeIndex)
        : null;
    const blockItems = allBlockItems.filter(graded);
    const evidence =
      laneEv && laneEv.built.length > 1 ? laneEv : groupEvidence(blockItems, sizeIndex);
    const bits = [phrase("pairsOutOfOrder", undefined, along)];
    if (evidence.offenders.length) bits.push(outOfPlace(evidence.offenders));
    if (avoidable) bits.push(phrase("biggerAbove", undefined, avoidable));
    const quantityLabel = options.labelAt?.(sizeIndex) ?? "Quantity";
    push(
      "size-order",
      node,
      ids,
      { title: sizeOrderTitle(shown(node), quantityLabel), detail: bits },
      true,
      {
        evidence:
          evidence.built.length > 1 ? { built: evidence.built, want: evidence.want } : undefined,
      },
    );
  });

  for (let depth = sizeIndex + 1; depth < plan.spine.length; depth++) {
    const kind: FlagKind = depth === terminalVariantDepth ? "flavour-order" : "he-order";
    const parents = new Map<string, PlanItem[]>();
    for (const item of all) {
      const key = item.path.slice(0, depth).join(PATH_SEPARATOR);
      const group = parents.get(key) ?? [];
      group.push(item);
      parents.set(key, group);
    }
    parents.forEach((group, node) => {
      const ids = group.map((item) => item.id);
      const sequence = cdtResolve.audit.sequenceBreaks(plan, ids, "horizontal");
      const read = sequence.order.map((id) => plan.get(id)).filter(Boolean) as PlanItem[];
      let bad = 0;
      for (let i = 1; i < read.length; i++) {
        const a = read[i - 1];
        const b = read[i];
        // Cross-shelf backwards is the shelf split, not a reorder defect.
        if (a.run !== b.run || a.shelf !== b.shelf) continue;
        // Placeholder has no order position; same skip as `groupsInOrder`.
        if (PLACEHOLDER_VALUE.test(String(a.path[depth] ?? ""))) continue;
        if (PLACEHOLDER_VALUE.test(String(b.path[depth] ?? ""))) continue;
        let firstDiff = -1;
        for (let level = 0; level < plan.spine.length; level++) {
          if (String(a.path[level]) !== String(b.path[level])) {
            firstDiff = level;
            break;
          }
        }
        if (firstDiff === depth && plan.cdtCompare(a, b) > 0) bad++;
      }
      const strays: string[] = [];
      const lanesByValue = new Map<string, Set<number>>();
      for (const item of group) {
        const value = String(item.path[depth] ?? "");
        const laneSet = lanesByValue.get(value) ?? new Set<number>();
        laneSet.add(item.run * 100 + item.shelf);
        lanesByValue.set(value, laneSet);
      }
      lanesByValue.forEach((laneSet, value) => {
        if (PLACEHOLDER_VALUE.test(value)) return;
        if (laneSet.size < 2) return;
        const shelves = [...laneSet].sort((a, b) => a - b);
        for (const lane of shelves) {
          const alone = shelves.every((other) => other === lane || Math.abs(other - lane) > 1);
          if (alone) {
            strays.push(value);
            break;
          }
        }
      });
      if (bad === 0 && strays.length === 0) return;
      const { built, want, offenders } = laneEvidence(read, depth);
      const label = labelAt(depth) ?? plan.spine[depth];
      const bits: FlagPhrase[] = [];
      if (bad) bits.push(pairsAgainstOrder(bad));
      if (strays.length) bits.push(phrase("strayFacing", { values: strays.join(", ") }));
      if (offenders.length) bits.push(outOfPlace(offenders));
      push(
        kind,
        node,
        group.map((item) => item.id),
        { title: phrase("flowTitle", { block: shown(node), label }), detail: bits, stop: true },
        true,
        { evidence: { built, want }, levelLabel: label },
      );
    });
  }

  // Same name at two levels is a mis-tag; repeats under one level are hierarchy.
  if (options.crossLevelNameFlags !== false) {
    // A size that also names a pack is the same quantity twice.
    const structuralDepth = sizeIndex >= 0 ? sizeIndex : plan.spine.length;
    const depthsByValue = new Map<string, Map<number, string[]>>();
    for (const item of all) {
      for (let depth = 0; depth < structuralDepth; depth++) {
        const value = String(item.path[depth] ?? "");
        if (!value || PLACEHOLDER_VALUE.test(value)) continue;
        const byDepth = depthsByValue.get(value) ?? new Map<number, string[]>();
        byDepth.set(depth, [...(byDepth.get(depth) ?? []), item.id]);
        depthsByValue.set(value, byDepth);
      }
    }
    const identityDepthSet = options.crossLevelIdentityDepths
      ? new Set(options.crossLevelIdentityDepths.map((depth) => depth - 1))
      : null;
    depthsByValue.forEach((byDepth, value) => {
      if (byDepth.size < 2) return;
      const depths = [...byDepth.keys()].sort((a, b) => a - b);
      // Configured roles: mis-tag needs the name at two identity levels.
      if (identityDepthSet && depths.filter((depth) => identityDepthSet.has(depth)).length < 2)
        return;
      const labelOf = (depth: number) => labelAt(depth) ?? plan.spine[depth];
      const exampleAt = (depth: number) => {
        const id = (byDepth.get(depth) ?? [])[0];
        const item = id ? plan.get(id) : undefined;
        return item
          ? shown(
              item.path
                .slice(0, depth + 1)
                .map((segment) => String(segment ?? ""))
                .join(PATH_SEPARATOR),
            )
          : value;
      };
      const uses = depths.map((depth) => `${labelOf(depth)} — ${exampleAt(depth)}`).join(" · ");
      push(
        "ambiguous",
        value,
        depths.flatMap((depth) => byDepth.get(depth) ?? []),
        {
          title: phrase("ambiguousTitle", { value, levels: depths.map(labelOf).join(", ") }),
          detail: [phrase("ambiguousDetail", { uses })],
        },
        false,
      );
    });
  }

  return { flags, pinByFlagId, bandHoleFlagIds, scatterFlagIds, splitStretches };
};

const levelSplits = (members: readonly StudioItem[], draft: StudioDraft, attr: string): boolean => {
  const seen = new Set<string>();
  for (const item of members) {
    const value = String(effectiveAttrs(item, draft)[attr] ?? "");
    if (isPlaceholderValue(value)) continue;
    seen.add(value);
    if (seen.size >= 2) return true;
  }
  return false;
};

interface BranchNode {
  path: string[];
  levels: readonly SpineLevel[];
  fromIndex: number;
}

const branchNodeOf = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  members: readonly StudioItem[],
): BranchNode => {
  let scope: readonly StudioItem[] = items;
  const path: string[] = [];
  let levelIndex = 0;
  let levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
  for (;;) {
    levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
    if (levelIndex >= levels.length) break;
    const attr = levels[levelIndex].attr;
    if (levelSplits(members, draft, attr)) break;
    levelIndex += 1;
    if (!levelSplits(scope, draft, attr)) continue;
    const value = String(effectiveAttrs(members[0], draft)[attr] ?? "");
    const narrowed = scope.filter(
      (item) => String(effectiveAttrs(item, draft)[attr] ?? "") === value,
    );
    if (narrowed.length === 0) break;
    scope = narrowed;
    path.push(value);
  }
  return { path, levels, fromIndex: levelIndex };
};

interface DrawnNode {
  attr: string;
  skuIds: readonly string[];
}

interface DrawnTree {
  nodes: Map<string, DrawnNode>;
  pathOf: Map<string, string[]>;
}

const drawnTree = (items: readonly StudioItem[], draft: StudioDraft): DrawnTree => {
  const nodes = new Map<string, DrawnNode>();
  const pathOf = new Map<string, string[]>();
  const walk = (members: readonly StudioItem[], levelIndex: number, path: string[]) => {
    for (const item of members) pathOf.set(item.skuId, path);
    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
    if (levelIndex >= levels.length) return;
    const level = levels[levelIndex];
    if (!levelSplits(members, draft, level.attr)) {
      walk(members, levelIndex + 1, path);
      return;
    }
    const groups = new Map<string, StudioItem[]>();
    for (const item of members) {
      const value = String(effectiveAttrs(item, draft)[level.attr] ?? "");
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    for (const [value, group] of groups) {
      const childPath = [...path, value];
      nodes.set(childPath.join(PATH_SEPARATOR), {
        attr: level.attr,
        skuIds: group.map((item) => item.skuId),
      });
      walk(group, levelIndex + 1, childPath);
    }
  };
  walk(items, 0, []);
  return { nodes, pathOf };
};

const treeLevelAttr = (depth: number): string => `__tree${depth}`;

/** Positional keys can pool members a branch separates; re-test each identity node. */
const rekeyPooledBandHoles = (
  flags: readonly Flag[],
  pinByFlagId: Readonly<Record<string, ResolverPin>>,
  items: readonly StudioItem[],
  draft: StudioDraft,
  blockIdentityAttributes: readonly string[] | undefined,
  bandHoleFlagIds: ReadonlySet<string> = new Set(),
  rankOf: (shelf: number) => number = (shelf) => shelf,
): { flags: Flag[]; pinByFlagId: Record<string, ResolverPin> } => {
  const identity = blockIdentityAttributes ?? [];
  if (identity.length === 0) return { flags: [...flags], pinByFlagId: { ...pinByFlagId } };
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const tree = drawnTree(items, draft);
  const identityPathOf = (skuId: string): string | null => {
    const item = bySku.get(skuId);
    if (!item) return null;
    const attrs = effectiveAttrs(item, draft);
    const rootAttr = draft.spine[0]?.attr;
    const rootValue = rootAttr ? String(attrs[rootAttr] ?? "") : "";
    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, [rootValue]);
    return levels
      .filter((level) => identity.includes(level.attr))
      .map((level) => String(attrs[level.attr] ?? ""))
      .filter((value) => value.length > 0 && !isPlaceholderValue(value))
      .join(PATH_SEPARATOR);
  };
  const outFlags: Flag[] = [];
  const outPins: Record<string, ResolverPin> = {};
  const keep = (flag: Flag) => {
    outFlags.push(flag);
    const pin = pinByFlagId[flag.id];
    if (pin) outPins[flag.id] = pin;
  };
  for (const flag of flags) {
    // A brand split-off offer is a question, not a census row: its products may span pack
    // formats (Tide Evo tiles and unit dose) and must not be rewritten into shelf-row rows.
    if (flag.kind !== "split" || flag.id.startsWith("split-off:")) {
      keep(flag);
      continue;
    }
    const groups = new Map<string, string[]>();
    let unresolved = false;
    for (const skuId of flag.skuIds) {
      const path = identityPathOf(skuId);
      if (path === null) {
        unresolved = true;
        break;
      }
      groups.set(path, [...(groups.get(path) ?? []), skuId]);
    }
    // Drawn paths, not identity — identity skips ornaments and placeholders.
    const namesATreeNode = flag.skuIds.every((skuId) => {
      const path = tree.pathOf.get(skuId);
      return !!path && flag.scopePath.every((segment, index) => path[index] === segment);
    });
    if (unresolved || groups.size <= 1 || (!bandHoleFlagIds.has(flag.id) && namesATreeNode)) {
      keep(flag);
      continue;
    }
    for (const [path, skuIds] of groups) {
      const shelvesByRun = new Map<number, Set<number>>();
      for (const skuId of skuIds) {
        for (const placement of bySku.get(skuId)?.placements ?? []) {
          const set = shelvesByRun.get(placement.run) ?? new Set<number>();
          set.add(placement.shelf);
          shelvesByRun.set(placement.run, set);
        }
      }
      const holes: number[] = [];
      const occupied = new Set<number>();
      for (const shelves of shelvesByRun.values()) {
        const ranks = [...shelves].sort((a, b) => a - b);
        for (const rank of ranks) occupied.add(rank);
        for (let rank = ranks[0]; rank <= ranks[ranks.length - 1]; rank += 1) {
          if (!shelves.has(rank)) holes.push(rank);
        }
      }
      if (holes.length === 0) continue;
      const id = `split:${path}`;
      if (outPins[id]) continue;
      const holeList = [...new Set(holes)].sort((a, b) => a - b);
      outFlags.push({
        ...flag,
        id,
        scopePath: path.split(PATH_SEPARATOR),
        skuIds,
        anchorSkuId: skuIds[0],
        ...worded(
          bandHoleWords(
            path,
            [...occupied].sort((a, b) => a - b).map(rankOf),
            holeList.map(rankOf),
          ),
        ),
      });
      const pin = pinByFlagId[flag.id];
      if (pin) outPins[id] = { ...pin, scope: path, node: path, ids: skuIds };
    }
  }
  return { flags: outFlags, pinByFlagId: outPins };
};

const splitPooledSizeOrder = (
  flags: readonly Flag[],
  pinByFlagId: Readonly<Record<string, ResolverPin>>,
  items: readonly StudioItem[],
  draft: StudioDraft,
  blockIdentityAttributes: readonly string[] | undefined,
  quantityAttribute: string,
): { flags: Flag[]; pinByFlagId: Record<string, ResolverPin> } => {
  const identity = blockIdentityAttributes ?? [];
  if (identity.length === 0) return { flags: [...flags], pinByFlagId: { ...pinByFlagId } };
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const identityPathOf = (skuId: string): string | null => {
    const item = bySku.get(skuId);
    if (!item) return null;
    const attrs = effectiveAttrs(item, draft);
    const rootAttr = draft.spine[0]?.attr;
    const rootValue = rootAttr ? String(attrs[rootAttr] ?? "") : "";
    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, [rootValue]);
    const quantityIndex = levels.findIndex((level) => level.attr === quantityAttribute);
    const nodeLevels =
      quantityIndex >= 0
        ? levels.slice(0, quantityIndex)
        : levels.filter((level) => identity.includes(level.attr));
    return nodeLevels
      .map((level) => String(attrs[level.attr] ?? ""))
      .filter((value) => value.length > 0 && !isPlaceholderValue(value))
      .join(PATH_SEPARATOR);
  };
  const outFlags: Flag[] = [];
  const outPins: Record<string, ResolverPin> = {};
  const keep = (flag: Flag) => {
    outFlags.push(flag);
    const pin = pinByFlagId[flag.id];
    if (pin) outPins[flag.id] = pin;
  };
  for (const flag of flags) {
    if (flag.kind !== "size-order") {
      keep(flag);
      continue;
    }
    const groups = new Map<string, string[]>();
    let unresolved = false;
    for (const skuId of flag.skuIds) {
      const path = identityPathOf(skuId);
      if (path === null) {
        unresolved = true;
        break;
      }
      groups.set(path, [...(groups.get(path) ?? []), skuId]);
    }
    if (unresolved || groups.size <= 1) {
      keep(flag);
      continue;
    }
    for (const [path, skuIds] of groups) {
      const placed = skuIds.flatMap((skuId) => {
        const item = bySku.get(skuId);
        if (!item) return [];
        const size = String(effectiveAttrs(item, draft)[quantityAttribute] ?? "");
        const parsed = sizeFamilyOf(size);
        return item.placements.map((placement) => ({
          skuId,
          size,
          family: parsed.family,
          magnitude: parsed.magnitude,
          run: placement.run,
          shelf: placement.shelf,
          x: placement.xCm,
        }));
      });
      // Along one shelf and one unit family — a wrap is not a step down.
      const lanes = new Map<string, typeof placed>();
      for (const entry of placed) {
        if (!(entry.magnitude > 0)) continue;
        const key = `${entry.family}|${entry.run}|${entry.shelf}`;
        lanes.set(key, [...(lanes.get(key) ?? []), entry]);
      }
      const ladders = [...lanes.values()]
        .filter((lane) => lane.length >= 2)
        .map((lane) => [...lane].sort((a, b) => a.x - b.x));
      const worst = ladders.find((lane) =>
        lane.some((entry, index) => index > 0 && entry.magnitude < lane[index - 1].magnitude),
      );
      if (!worst) continue;
      const id = `size-order:${path}`;
      if (outPins[id]) continue;
      const wanted = [...new Set(worst.map((entry) => entry.size))].sort(
        (a, b) => sizeFamilyOf(a).magnitude - sizeFamilyOf(b).magnitude,
      );
      outFlags.push({
        ...flag,
        id,
        scopePath: path.split(PATH_SEPARATOR),
        skuIds: [...new Set(worst.map((entry) => entry.skuId))],
        anchorSkuId: worst[0].skuId,
        ...retitled(flag, phrase("sizeOrderTitle", { block: path })),
        evidence: { built: worst.map((entry) => entry.size), want: wanted },
      });
      const pin = pinByFlagId[flag.id];
      if (pin) {
        outPins[id] = {
          ...pin,
          scope: path,
          node: path,
          ids: [...new Set(worst.map((entry) => entry.skuId))],
        };
      }
    }
  }
  return { flags: outFlags, pinByFlagId: outPins };
};

/** Depth is from the flag id — `scopePath` is compacted. */
const BELOW_SIZE_ORDER_KINDS = new Set<FlagKind>(["he-order", "flavour-order"]);

/** Recensus on the draft's drawn tree; `nodeKey` is positional and would grade stale cuts. */
const rescopeScatterRows = (
  flags: readonly Flag[],
  pinByFlagId: Readonly<Record<string, ResolverPin>>,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  scatterFlagIds: ReadonlySet<string>,
  blockIdentityAttributes: readonly string[] | undefined,
): { flags: Flag[]; pinByFlagId: Record<string, ResolverPin> } => {
  const kept = flags.filter((flag) => !scatterFlagIds.has(flag.id));
  const keptPins: Record<string, ResolverPin> = {};
  for (const flag of kept) {
    const pin = pinByFlagId[flag.id];
    if (pin) keptPins[flag.id] = pin;
  }
  const cdtResolve = (globalThis as { CDTResolve?: CdtResolveInternals }).CDTResolve;
  if (!cdtResolve) return { flags: [...flags], pinByFlagId: { ...pinByFlagId } };
  if (Object.keys(draft.scopedLevelOrder ?? {}).length === 0) {
    return { flags: [...flags], pinByFlagId: { ...pinByFlagId } };
  }

  const identity = blockIdentityAttributes ?? draft.spine.map((level) => level.attr);
  const deepIdentity = new Set(
    identity.filter((attr) => draft.spine.findIndex((level) => level.attr === attr) >= 2),
  );
  if (deepIdentity.size === 0) return { flags: kept, pinByFlagId: keptPins };

  const { nodes, pathOf } = drawnTree(items, draft);
  const depth = Math.max(0, ...[...pathOf.values()].map((path) => path.length));
  if (depth === 0) return { flags: kept, pinByFlagId: keptPins };

  const projected = items.map((item) => {
    const path = pathOf.get(item.skuId) ?? [];
    const attrs: Record<string, string | null> = {};
    path.forEach((value, index) => {
      attrs[treeLevelAttr(index)] = value;
    });
    return { ...item, attrs };
  });
  const projectedDraft: StudioDraft = {
    ...draft,
    spine: Array.from({ length: depth }, (_, index) => ({
      attr: treeLevelAttr(index),
      label: treeLevelAttr(index),
      orient: "horizontal" as const,
    })),
    valueOrder: {},
    scopedValueOrder: {},
    attrOverrides: {},
    levelBands: undefined,
    scopedLevelOrder: undefined,
  };
  const inputs = toResolverInputs(projected, runs, projectedDraft, []);
  let plan: PlanModel;
  try {
    plan = cdtResolve.loadPlan(inputs.canvas, inputs.itemsDoc, { runW: inputs.runW });
  } catch {
    return { flags: [...flags], pinByFlagId: { ...pinByFlagId } };
  }

  const censusDepths = new Set<number>();
  for (const [key, node] of nodes) {
    if (!deepIdentity.has(node.attr)) continue;
    censusDepths.add(key.split(PATH_SEPARATOR).length);
  }
  if (censusDepths.size === 0) return { flags: kept, pinByFlagId: keptPins };

  const alreadyFlagged = new Set(
    kept.filter((flag) => flag.kind === "split").map((flag) => [...flag.skuIds].sort().join("")),
  );

  const attrsOf = new Map<string, Record<string, string | null>>();
  const attrOf = (item: StudioItem, attr: string): string => {
    let resolved = attrsOf.get(item.skuId);
    if (!resolved) {
      resolved = effectiveAttrs(item, draft);
      attrsOf.set(item.skuId, resolved);
    }
    return String(resolved[attr] ?? "");
  };
  const bySku = new Map(items.map((item) => [item.skuId, item]));

  const positionalKeyOf = (skuIds: readonly string[]): string | null => {
    const members = skuIds.flatMap((skuId) => bySku.get(skuId) ?? []);
    if (members.length !== skuIds.length || members.length === 0) return null;
    const wanted = new Set(skuIds);
    for (let cut = 1; cut <= draft.spine.length; cut += 1) {
      const key = draft.spine
        .slice(0, cut)
        .map((level) => attrOf(members[0], level.attr))
        .join(PATH_SEPARATOR);
      const matched = items.filter(
        (item) =>
          draft.spine
            .slice(0, cut)
            .map((level) => attrOf(item, level.attr))
            .join(PATH_SEPARATOR) === key,
      );
      if (matched.length !== wanted.size) continue;
      if (matched.every((item) => wanted.has(item.skuId))) return key;
    }
    return null;
  };

  const found: Flag[] = [];
  const foundPins: Record<string, ResolverPin> = {};
  for (const scatter of scatterSplits(
    cdtResolve,
    plan,
    [...censusDepths].sort((a, b) => a - b),
  )) {
    const node = nodes.get(scatter.node);
    if (!node || !deepIdentity.has(node.attr)) continue;
    const skuIds = [...new Set(scatter.ids.map(baseSkuId))];
    if (alreadyFlagged.has([...skuIds].sort().join(""))) continue;
    const scopePath = scatter.node.split(PATH_SEPARATOR).filter((segment) => segment.length > 0);
    const id = `split:${scatter.node}`;
    if (found.some((flag) => flag.id === id)) continue;
    found.push({
      id,
      kind: "split",
      severity: FLAG_SEVERITY.split,
      scopePath,
      ...worded(scatterWords(scopePath, scatter.blocks)),
      skuIds,
      anchorSkuId: skuIds[0] ?? "",
      evidence: {},
      fix: null,
    });
    const positional = positionalKeyOf(skuIds);
    if (positional !== null) {
      foundPins[id] = {
        kind: "orphan",
        scope: positional,
        node: positional,
        ids: scatter.ids,
        fragment: true,
      };
    }
  }
  return { flags: [...kept, ...found], pinByFlagId: { ...keptPins, ...foundPins } };
};

interface NodeOrderVerdict {
  bad: number;
  built: string[];
  want: string[];
  offenders: string[];
}

const gradeLanesAgainstOrder = (
  members: readonly StudioItem[],
  draft: StudioDraft,
  attr: string,
  order: readonly string[],
): NodeOrderVerdict | null => {
  if (order.length < 2) return null;
  const rank = new Map(order.map((value, index) => [value, index]));
  const lanes = new Map<string, { x: number; value: string }[]>();
  for (const item of members) {
    const value = String(effectiveAttrs(item, draft)[attr] ?? "");
    for (const placement of item.placements) {
      const key = `${placement.run}|${placement.shelf}`;
      lanes.set(key, [...(lanes.get(key) ?? []), { x: placement.xCm, value }]);
    }
  }
  let bad = 0;
  let worst: { x: number; value: string }[] = [];
  let worstBad = 0;
  for (const lane of lanes.values()) {
    lane.sort((a, b) => a.x - b.x);
    let laneBad = 0;
    for (let index = 1; index < lane.length; index += 1) {
      const before = lane[index - 1].value;
      const after = lane[index].value;
      if (before === after) continue;
      if (PLACEHOLDER_VALUE.test(before) || PLACEHOLDER_VALUE.test(after)) continue;
      const left = rank.get(before);
      const right = rank.get(after);
      if (left === undefined || right === undefined) continue;
      if (left > right) laneBad += 1;
    }
    bad += laneBad;
    if (laneBad > worstBad) {
      worstBad = laneBad;
      worst = lane;
    }
  }
  if (bad === 0) return null;

  const built: string[] = [];
  for (const entry of worst) {
    if (built[built.length - 1] !== entry.value) built.push(entry.value);
  }
  const firstSeen = [...new Set(built)];
  const want = [...firstSeen].sort(
    (a, b) => (rank.get(a) ?? order.length) - (rank.get(b) ?? order.length),
  );
  const returned = new Set<string>();
  const seen = new Set<string>();
  for (const value of built) {
    if (seen.has(value)) returned.add(value);
    seen.add(value);
  }
  const offenders = [
    ...new Set([...firstSeen.filter((value, index) => value !== want[index]), ...returned]),
  ];
  return { bad, built, want, offenders };
};

const reconcileBelowSizeOrderFlags = (
  flags: readonly Flag[],
  pinByFlagId: Readonly<Record<string, ResolverPin>>,
  items: readonly StudioItem[],
  draft: StudioDraft,
): { flags: Flag[]; pinByFlagId: Record<string, ResolverPin> } => {
  const bySku = new Map(items.map((item) => [item.skuId, item]));

  const studioKeyOf = (attr: string, anchor: StudioItem): string | null => {
    const discriminates = (scope: readonly StudioItem[], levelAttr: string): boolean =>
      levelSplits(scope, draft, levelAttr);
    let scope: readonly StudioItem[] = items;
    const path: string[] = [];
    let levelIndex = 0;
    for (;;) {
      const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
      if (levelIndex >= levels.length) return null;
      const level = levels[levelIndex];
      if (level.attr === attr) return path.join(PATH_SEPARATOR);
      levelIndex += 1;
      if (!discriminates(scope, level.attr)) continue;
      const value = String(effectiveAttrs(anchor, draft)[level.attr] ?? "");
      scope = scope.filter(
        (item) => String(effectiveAttrs(item, draft)[level.attr] ?? "") === value,
      );
      path.push(value);
      if (scope.length === 0) return null;
    }
  };

  const readsInOrder = (
    members: readonly StudioItem[],
    attr: string,
    order: readonly string[],
  ): boolean => {
    const rank = new Map(order.map((value, index) => [value, index]));
    const lanes = new Map<string, { x: number; value: string }[]>();
    for (const item of members) {
      const value = String(effectiveAttrs(item, draft)[attr] ?? "");
      for (const placement of item.placements) {
        const key = `${placement.run}|${placement.shelf}`;
        lanes.set(key, [...(lanes.get(key) ?? []), { x: placement.xCm, value }]);
      }
    }
    for (const lane of lanes.values()) {
      lane.sort((a, b) => a.x - b.x);
      for (let i = 1; i < lane.length; i += 1) {
        const a = lane[i - 1];
        const b = lane[i];
        if (a.value === b.value) continue;
        if (PLACEHOLDER_VALUE.test(a.value) || PLACEHOLDER_VALUE.test(b.value)) continue;
        const left = rank.get(a.value);
        const right = rank.get(b.value);
        // Unlisted values cannot be judged by this order.
        if (left === undefined || right === undefined) return false;
        if (left > right) return false;
      }
    }
    return true;
  };

  /** Inferred-from-as-built scoped orders would always "match" — only re-read real disagreement. */
  const disagreesWithPlan = (
    scoped: readonly string[],
    attr: string,
    members: readonly StudioItem[],
  ): boolean => {
    const present = new Set(members.map((item) => String(effectiveAttrs(item, draft)[attr] ?? "")));
    const here = (values: readonly string[]) => values.filter((value) => present.has(value));
    const mine = here(scoped);
    const plan = here(draft.valueOrder[attr] ?? []);
    if (mine.length !== plan.length) return true;
    return mine.some((value, index) => value !== plan[index]);
  };

  const hasStray = (members: readonly StudioItem[], attr: string): boolean => {
    const lanesByValue = new Map<string, Set<number>>();
    for (const item of members) {
      const value = String(effectiveAttrs(item, draft)[attr] ?? "");
      if (!value || PLACEHOLDER_VALUE.test(value)) continue;
      for (const placement of item.placements) {
        const set = lanesByValue.get(value) ?? new Set<number>();
        set.add(placement.run * 100 + placement.shelf);
        lanesByValue.set(value, set);
      }
    }
    for (const laneSet of lanesByValue.values()) {
      if (laneSet.size < 2) continue;
      const lanes = [...laneSet].sort((a, b) => a - b);
      if (
        lanes.some((lane) => lanes.every((other) => other === lane || Math.abs(other - lane) > 1))
      ) {
        return true;
      }
    }
    return false;
  };

  const outFlags: Flag[] = [];
  const outPins: Record<string, ResolverPin> = {};
  for (const flag of flags) {
    const keep = () => {
      outFlags.push(flag);
      const pin = pinByFlagId[flag.id];
      if (pin) outPins[flag.id] = pin;
    };
    if (!BELOW_SIZE_ORDER_KINDS.has(flag.kind)) {
      keep();
      continue;
    }
    const rawScope = flag.id.slice(flag.kind.length + 1);
    const depth = rawScope.split(PATH_SEPARATOR).length;
    const attr = draft.spine[depth]?.attr;
    const members = flag.skuIds.flatMap((skuId) => bySku.get(skuId) ?? []);
    if (attr === undefined || members.length === 0) {
      keep();
      continue;
    }
    const rootAttr = draft.spine[0]?.attr;
    const rootValue = rootAttr ? String(effectiveAttrs(members[0], draft)[rootAttr] ?? "") : "";
    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, [rootValue]);
    const branchIndex = levels.findIndex((level) => level.attr === attr);
    if (branchIndex >= 0 && branchIndex < depth) continue;
    const studioKey = draft.scopedValueOrder?.[rawScope] ? rawScope : studioKeyOf(attr, members[0]);
    const scoped =
      draft.scopedValueOrder?.[rawScope] ??
      (studioKey !== null ? draft.scopedValueOrder?.[studioKey] : undefined);
    if (scoped && scoped.length > 0 && disagreesWithPlan(scoped, attr, members)) {
      if (!hasStray(members, attr) && readsInOrder(members, attr, scoped)) continue;
    }
    // No scoped order: this node's shelf is the order. No pin — vendored resequence uses the pool.
    const carriers = members.map((item) => ({
      value: String(effectiveAttrs(item, draft)[attr] ?? ""),
      placements: item.placements,
    }));
    const keys = studioKey !== null && studioKey !== rawScope ? [rawScope, studioKey] : [rawScope];
    const nodeOrder = nodeValueOrder(draft, keys, attr, carriers);
    const verdict = gradeLanesAgainstOrder(members, draft, attr, nodeOrder);
    if (!verdict) {
      if (!hasStray(members, attr)) continue;
      keep();
      continue;
    }
    const anchor =
      members.find((item) =>
        verdict.offenders.includes(String(effectiveAttrs(item, draft)[attr] ?? "")),
      ) ?? members[0];
    const bits = [pairsAgainstOrder(verdict.bad)];
    if (verdict.offenders.length) bits.push(outOfPlace(verdict.offenders));
    const said = { detail: bits, stop: true };
    outFlags.push({
      ...flag,
      detail: detailText(said, englishFlagTemplate),
      ...(flag.words ? { words: { ...flag.words, ...said } } : {}),
      anchorSkuId: baseSkuId(anchor.skuId),
      evidence: { built: verdict.built, want: verdict.want },
      fix: null,
      fixLabel: null,
    });
  }
  return { flags: outFlags, pinByFlagId: outPins };
};

/** No pin — vendored pin would resequence by the pool. */
const detectBranchLevelOrder = (
  flags: readonly Flag[],
  items: readonly StudioItem[],
  draft: StudioDraft,
  quantityAttribute: string,
  blockIdentityAttributes: readonly string[] | undefined,
  terminalVariantAttribute?: string,
): Flag[] => {
  const globalQuantity = draft.spine.findIndex((level) => level.attr === quantityAttribute);
  if (globalQuantity < 0 || !blockIdentityAttributes) return [...flags];
  const identity = new Set(blockIdentityAttributes);

  const attrsOf = new Map<string, Record<string, string | null>>();
  const attrs = (item: StudioItem): Record<string, string | null> => {
    const cached = attrsOf.get(item.skuId);
    if (cached) return cached;
    const resolved = effectiveAttrs(item, draft);
    attrsOf.set(item.skuId, resolved);
    return resolved;
  };
  const valueOf = (item: StudioItem, attr: string): string => String(attrs(item)[attr] ?? "");

  const discriminates = (members: readonly StudioItem[], attr: string): boolean =>
    levelSplits(members, draft, attr);

  const ids = new Set(flags.map((flag) => flag.id));
  const found: Flag[] = [];

  const gradeNode = (
    members: readonly StudioItem[],
    path: readonly string[],
    level: { attr: string; label: string },
  ) => {
    const pathKey = path.join(PATH_SEPARATOR);
    const order = nodeValueOrder(
      draft,
      [pathKey],
      level.attr,
      members.map((item) => ({ value: valueOf(item, level.attr), placements: item.placements })),
    );
    const verdict = gradeLanesAgainstOrder(members, draft, level.attr, order);
    if (!verdict) return;
    const { bad, built, want, offenders } = verdict;

    const kind: FlagKind =
      terminalVariantAttribute && level.attr === terminalVariantAttribute
        ? "flavour-order"
        : "he-order";
    const id = `${kind}:${pathKey}`;
    if (ids.has(id)) return;
    ids.add(id);
    const scopePath = path.filter((segment) => segment.trim().length > 0);
    const skuIds = [...new Set(members.map((item) => baseSkuId(item.skuId)))];
    const anchor =
      members.find((item) => offenders.includes(valueOf(item, level.attr))) ?? members[0];
    const bits = [pairsAgainstOrder(bad)];
    if (offenders.length) bits.push(outOfPlace(offenders));
    found.push({
      id,
      kind,
      levelLabel: level.label,
      severity: FLAG_SEVERITY[kind],
      scopePath,
      ...worded({
        title: phrase("flowTitle", {
          block: collapsePathRepeats(scopePath).join(PATH_SEPARATOR) || phrase("thisPlan"),
          label: level.label,
        }),
        detail: bits,
        stop: true,
      }),
      skuIds,
      anchorSkuId: baseSkuId(anchor.skuId),
      evidence: { built, want },
      fix: null,
    });
  };

  const walk = (members: readonly StudioItem[], levelIndex: number, path: readonly string[]) => {
    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
    if (levelIndex >= levels.length || members.length <= 1) return;
    const level = levels[levelIndex];
    if (!discriminates(members, level.attr)) {
      walk(members, levelIndex + 1, path);
      return;
    }
    const quantityIndex = levels.findIndex((entry) => entry.attr === quantityAttribute);
    if (quantityIndex > levelIndex && !identity.has(level.attr)) {
      gradeNode(members, path, level);
    }
    const groups = new Map<string, StudioItem[]>();
    for (const item of members) {
      const value = valueOf(item, level.attr);
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    for (const [value, group] of groups) walk(group, levelIndex + 1, [...path, value]);
  };
  walk(items, 0, []);

  return [...flags, ...found];
};

const treeDrawsLevelBelow = (
  members: readonly StudioItem[],
  draft: StudioDraft,
  branch: readonly SpineLevel[],
  fromIndex: number,
  attr: string,
): boolean => {
  const valuesOf = (group: readonly StudioItem[], of: string): string[] =>
    group
      .map((item) => String(effectiveAttrs(item, draft)[of] ?? ""))
      .filter((value) => !isPlaceholderValue(value));
  const splits = (group: readonly StudioItem[], of: string): boolean =>
    new Set(valuesOf(group, of)).size >= 2;

  let groups: (readonly StudioItem[])[] = [members];
  for (let index = fromIndex; index < branch.length; index += 1) {
    const level = branch[index];
    if (level.attr === attr) return groups.some((group) => splits(group, attr));
    if (!groups.some((group) => splits(group, level.attr))) continue;
    groups = groups.flatMap((group) => {
      const byValue = new Map<string, StudioItem[]>();
      for (const item of group) {
        const value = String(effectiveAttrs(item, draft)[level.attr] ?? "");
        const bucket = byValue.get(value);
        if (bucket) bucket.push(item);
        else byValue.set(value, [item]);
      }
      return [...byValue.values()];
    });
  }
  return false;
};

/** Candidates are the spine, never raw workbook keys. */
const suggestStructure = (
  flags: readonly Flag[],
  items: readonly StudioItem[],
  draft: StudioDraft,
  quantityAttribute: string,
  labelOfAttr: (attr: string) => string,
  declared: {
    values: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
    labels: Readonly<Record<string, string>>;
  } = { values: {}, labels: {} },
): Flag[] => {
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const candidateAttrs = draft.spine.map((level) => level.attr);
  const liveAttrs = new Set(
    items.flatMap((item) =>
      Object.entries(item.attrs)
        .filter(([, value]) => value != null)
        .map(([attr]) => attr),
    ),
  );
  const declaredOnly = Object.keys(declared.labels).filter((attr) => !liveAttrs.has(attr));
  return flags.map((flag) => {
    if (flag.kind !== "size-order") return flag;
    const members = flag.skuIds.flatMap((skuId) => bySku.get(skuId) ?? []);
    if (members.length !== flag.skuIds.length || members.length === 0) return flag;
    const branchNode = branchNodeOf(items, draft, members);
    const branch = branchNode.levels;
    // Already nested here — not missing.
    const above = new Set(branch.slice(0, branchNode.fromIndex).map((level) => level.attr));
    const explanation = explainOrderViolation(
      members,
      candidateAttrs.filter((attr) => !above.has(attr)),
      quantityAttribute,
    );
    if (!explanation) {
      for (const attr of declaredOnly) {
        const shadow = members.map((item) => ({
          ...item,
          attrs: {
            ...item.attrs,
            [attr]: declared.values[item.itemMasterId ?? item.skuId]?.[attr] ?? null,
          },
        }));
        const declaredExplanation = explanationBy(shadow, attr, quantityAttribute);
        if (!declaredExplanation) continue;
        const gapLabel = declared.labels[attr] ?? attr;
        return {
          ...flag,
          severity: "toReview" as const,
          ...worded({
            title: phrase("dataGapTitle", { label: gapLabel }),
            detail: [
              phrase("dataGapDetail", {
                label: gapLabel,
                level: gapLabel.toLowerCase(),
                values: declaredExplanation.values.join(", "),
              }),
            ],
          }),
        };
      }
      return flag;
    }
    const label = labelOfAttr(explanation.attr);
    const scope = flag.scopePath.join(PATH_SEPARATOR);
    const fromIndex = branchNode.fromIndex < branch.length ? branchNode.fromIndex : -1;
    const nextLevel = fromIndex < 0 ? undefined : branch[fromIndex].attr;
    const node = flag.scopePath[flag.scopePath.length - 1] ?? phrase("thisBlock");
    const level = label.toLowerCase();
    const values = explanation.values.join(", ");
    if (nextLevel === explanation.attr) {
      return {
        ...flag,
        severity: "toReview" as const,
        ...worded({
          title: phrase("inOrderTitle", { level }),
          detail: [phrase("inOrderDetail", { values })],
        }),
      };
    }
    // Carried lower down — lift, do not claim missing.
    if (
      fromIndex >= 0 &&
      treeDrawsLevelBelow(members, draft, branch, fromIndex, explanation.attr)
    ) {
      const overLabel = labelOfAttr(nextLevel as string).toLowerCase();
      return {
        ...flag,
        severity: "toReview" as const,
        ...worded({
          title: phrase("groupFirstTitle", { level, over: overLabel, scope }),
          detail: [phrase("groupFirstDetail", { level, values, over: overLabel })],
          fixLabel: phrase("groupFirstFix", {
            label,
            next: labelOfAttr(nextLevel as string),
            node,
          }),
        }),
      };
    }
    return {
      ...flag,
      severity: "toReview" as const,
      ...worded({
        title: phrase("splitByTitle", { scope, level }),
        detail: [phrase("splitByDetail", { level, values, node })],
        fixLabel: phrase("splitByFix", { node, label }),
      }),
    };
  });
};

/** Findings that are about the SHAPE of a block, and so can be explained by a band. */
const BAND_EXPLAINABLE: ReadonlySet<FlagKind> = new Set<FlagKind>([
  "split",
  "contiguity",
  "ragged",
  "column",
  "orphan",
]);

type BandCondition = "MUST_HAVE" | "MUST_HAVE_ONLY" | "MUST_NOT_HAVE" | "MAY_HAVE" | "CAN_HAVE";

/** The conditions that put products somewhere or keep them out, and so can break a block. */
const FENCING_CONDITIONS: ReadonlySet<BandCondition> = new Set<BandCondition>([
  "MUST_HAVE",
  "MUST_HAVE_ONLY",
  "MUST_NOT_HAVE",
]);

type FencingBand = {
  condition: BandCondition;
  skuIds: readonly string[];
  cells: readonly { run?: number; shelf: number; bays: readonly number[] }[];
};

/**
 * Drop the shape findings an accepted band accounts for.
 *
 * A band fences a group of products onto given shelves. Doing so necessarily breaks the block
 * those products would otherwise have formed, so reporting that break as a defect blames the
 * plan for obeying the merchant. A finding is dropped only when EVERY product it names is
 * governed by ONE band that actually fences cells, and the finding reaches one of that band's
 * cells (a product in or next to it, or the pieces either side of it). A finding that reaches
 * past the band, names a product the band does not govern, or breaks away from the band's cells
 * is left alone.
 *
 * Only a band that puts products somewhere or keeps them out (MUST_HAVE, MUST_HAVE_ONLY,
 * MUST_NOT_HAVE) can cause a split. MAY_HAVE is neutral and CAN_HAVE (a split-off or breakout)
 * only allows, so neither explains why a block is in pieces and their products keep their flags.
 */
const withoutBandDrivenFlags = (
  flags: readonly Flag[],
  bands: readonly FencingBand[] | undefined,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
): readonly Flag[] => {
  const fencing = (bands ?? []).filter(
    (band) =>
      FENCING_CONDITIONS.has(band.condition) && band.cells.length > 0 && band.skuIds.length > 0,
  );
  if (fencing.length === 0) return flags;
  const governed = fencing.map((band) => new Set(band.skuIds));
  const bayWidthOf = new Map(runs.map((run) => [run.run, run.bayWidthCm]));
  // Bays as the score reads them, so a band explains exactly what it grades.
  const spotsOf = new Map(
    items.map((item) => [
      item.skuId,
      item.placements.map((placement) => {
        const bayWidth = bayWidthOf.get(placement.run) || 0;
        return {
          run: placement.run,
          shelf: placement.shelf,
          bay: placement.bay ?? (bayWidth > 0 ? Math.floor(placement.xCm / bayWidth) + 1 : 1),
        };
      }),
    ]),
  );
  type Spot = { run: number; shelf: number; bay: number };
  type Cell = FencingBand["cells"][number];
  const onRun = (cell: Cell, spot: Spot) => cell.run === undefined || cell.run === spot.run;
  const inBay = (cell: Cell, bay: number) => cell.bays.length === 0 || cell.bays.includes(bay);
  const explains = (band: FencingBand, skuIds: readonly string[]) => {
    const spots = skuIds.flatMap((skuId) => spotsOf.get(skuId) ?? []);
    const inCell = (spot: Spot) =>
      band.cells.some(
        (cell) => onRun(cell, spot) && cell.shelf === spot.shelf && inBay(cell, spot.bay),
      );
    // A fenced-out product standing in its cell is a breach, not the band's doing.
    if (band.condition === "MUST_NOT_HAVE" && spots.some(inCell)) return false;
    // The break must reach a cell: one stands in it or beside it, or the pieces enclose it.
    return band.cells.some((cell) => {
      const near = spots.filter((spot) => onRun(cell, spot));
      if (near.length === 0) return false;
      const touches = near.some(
        (spot) =>
          (Math.abs(cell.shelf - spot.shelf) <= 1 && inBay(cell, spot.bay)) ||
          (cell.shelf === spot.shelf &&
            [spot.bay - 1, spot.bay + 1].some((bay) => inBay(cell, bay))),
      );
      const shelves = near.map((spot) => spot.shelf);
      const bays = near.map((spot) => spot.bay);
      const enclosed =
        cell.shelf >= Math.min(...shelves) &&
        cell.shelf <= Math.max(...shelves) &&
        (cell.bays.length === 0 ||
          cell.bays.some((bay) => bay >= Math.min(...bays) && bay <= Math.max(...bays)));
      return touches || enclosed;
    });
  };
  return flags.filter((flag) => {
    if (!BAND_EXPLAINABLE.has(flag.kind) || flag.skuIds.length === 0) return true;
    return !fencing.some(
      (band, index) =>
        flag.skuIds.every((skuId) => governed[index].has(skuId)) && explains(band, flag.skuIds),
    );
  });
};

export const detectExceptions = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  options: Pick<DetectCoreOptions, "gradientPins"> & {
    quantityAttribute?: string;
    blockIdentityAttributes?: readonly string[];
    verticalBlockShare?: number;
    terminalVariantAttribute?: string;
    /** Studio passes false: cross-level name repeats are sheet noise, not shelf findings. */
    crossLevelNameFlags?: boolean;
    declaredValues?: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
    declaredLabels?: Readonly<Record<string, string>>;
    /** The brand role. Named, a brand in stretches one sub-brand explains is offered as two
     *  brands; absent, it stays the plain stretches violation. */
    brandAttribute?: string;
    /** Split-off offers the merchant answered No to, by flag id: still raised, marked declined,
     *  but the plain row, its pin and the products' stray rows stay. */
    declinedSplitOffIds?: ReadonlySet<string>;
    /** Read off the base planogram only: raise every brand block whose rows do not give the base
     *  back, with the line that explains it as an offer, else as a question (`rowLineFlags`). */
    rowReplication?: boolean;
    /** Bands the merchant accepted. A group deliberately fenced onto given shelves is not a
     *  broken block: a shape finding whose products are ALL governed by one band is the band
     *  doing its job, so it is dropped rather than shown as a defect. Findings that are not
     *  about block shape, and findings that reach beyond one band, are untouched. Only a band
     *  whose condition fences (see withoutBandDrivenFlags) explains anything away. */
    bands?: readonly FencingBand[];
  } = {},
): DetectedExceptions => {
  const bare = toResolverInputs(items, runs, draft, [], {
    quantityAttribute: options.quantityAttribute,
    blockIdentityAttributes: options.blockIdentityAttributes,
    verticalBlockShare: options.verticalBlockShare,
    terminalVariantAttribute: options.terminalVariantAttribute,
  });
  // The canvas and these passes read compact ranks: empty hang lines must not part a block.
  const rows = compactRowsOf(runs, items);
  const core = detectCore(bare.canvas, bare.itemsDoc, {
    runW: bare.runW,
    bayW: planBayWidthOf(runs),
    runs: rows.runs,
    labelAt: (depth) => draft.spine[depth]?.label,
    gradientPins: options.gradientPins,
    crossLevelNameFlags: options.crossLevelNameFlags,
    sizeRule: bare.sizeRule,
    orphanDepths: options.blockIdentityAttributes ? bare.blockIdentityDepths : undefined,
    crossLevelIdentityDepths: options.blockIdentityAttributes
      ? bare.blockIdentityDepths
      : undefined,
    terminalVariantDepth: options.terminalVariantAttribute ? bare.terminalVariantDepth : undefined,
    rankOfShelf: bare.rankOfShelf,
  });
  const splitOff = options.brandAttribute
    ? splitOffBrands(core.flags, core.pinByFlagId, {
        items: rows.items,
        runs: rows.runs,
        draft,
        brandAttr: options.brandAttribute,
        quantityAttr: options.quantityAttribute,
        structuralAttrs: options.blockIdentityAttributes,
        stretches: core.splitStretches,
        declinedOfferIds: options.declinedSplitOffIds,
      })
    : core;
  const scatter = rescopeScatterRows(
    splitOff.flags,
    splitOff.pinByFlagId,
    rows.items,
    rows.runs,
    draft,
    core.scatterFlagIds,
    options.blockIdentityAttributes,
  );
  // Scatter rows are the core's own plus the ones the recensus found; a split-off offer covering
  // a product replaces its stray and scatter rows.
  const beforeRescope = new Set(splitOff.flags.map((flag) => flag.id));
  const explained = options.brandAttribute
    ? dropExplainedStrays(
        scatter.flags,
        scatter.pinByFlagId,
        new Set([
          ...core.scatterFlagIds,
          ...scatter.flags.filter((flag) => !beforeRescope.has(flag.id)).map((flag) => flag.id),
        ]),
      )
    : scatter;
  const rekeyed = rekeyPooledBandHoles(
    explained.flags,
    explained.pinByFlagId,
    rows.items,
    draft,
    options.blockIdentityAttributes,
    core.bandHoleFlagIds,
    rows.rankOf,
  );
  const sized = splitPooledSizeOrder(
    rekeyed.flags,
    rekeyed.pinByFlagId,
    rows.items,
    draft,
    options.blockIdentityAttributes,
    options.quantityAttribute ?? "",
  );
  const scoped = reconcileBelowSizeOrderFlags(sized.flags, sized.pinByFlagId, rows.items, draft);
  const shaped = detectBranchLevelOrder(
    suggestStructure(
      scoped.flags,
      rows.items,
      draft,
      options.quantityAttribute ?? "",
      (attr) => draft.spine.find((level) => level.attr === attr)?.label ?? attr,
      { values: options.declaredValues ?? {}, labels: options.declaredLabels ?? {} },
    ),
    rows.items,
    draft,
    options.quantityAttribute ?? "",
    options.blockIdentityAttributes,
    options.terminalVariantAttribute,
  );
  const detected = withoutBandDrivenFlags(shaped, options.bands, items, runs);
  const flags = options.rowReplication
    ? [...detected, ...rowLineFlags(items, draft, runs)]
    : detected;
  const pinByFlagId = scoped.pinByFlagId;
  const inputs: ResolverInputs = {
    ...bare,
    pins: Object.values(pinByFlagId),
    pinByFlagId,
    // Fragment pins stay off the canvas — orphan verdict would roll the rejoin back.
    canvas: {
      ...bare.canvas,
      pins: Object.values(pinByFlagId).filter((pin) => !pin.fragment),
    },
  };
  return { flags, pinByFlagId, inputs };
};
